import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ClipboardPen } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { dkMessage, fmtDay, fmtPct, fmtQuantity, fmtSigned, pctOf, unitOf } from "./dkUtils";
import { openingSourceText } from "./DayLine";
import { OfflineBox, dataStateText } from "./widgets";

const P = OPS.dk.dayPanel;

function Row({ k, label, value, unit, tone = "" }) {
    const empty = value === "" || value === null || value === undefined;
    return (
        <div className="min-w-0">
            <dt className="truncate text-[10px] font-bold text-[color:var(--ink-muted)]">{label}</dt>
            <dd data-k={k} className={`truncate text-xs font-bold ${tone || "text-[color:var(--ink)]"}`}>
                {empty ? <span className="text-[color:var(--ink-faint)]">—</span> : <><bdi dir="ltr">{value}</bdi> <span className="text-[10px] font-medium text-[color:var(--ink-muted)]">{unit}</span></>}
            </dd>
        </div>
    );
}

/** The month grid's day detail: one location × one day, read-only, from `GET /dk/day`,
 *  with «فتح في اليوم» (to edit it) when the caller has the day tab. */
export function DayPanel({ locationId, locationName, date, scope, canOpenDay, onClose }) {
    const { t, tn, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const open = !!(locationId && date);
    // Closing clears `date` one render before the effect below clears `data`,
    // and the sheet keeps its content on screen while it animates out: render
    // that last frame with the date the data belongs to, never with null.
    const shownDate = useRef(date);
    if (date) shownDate.current = date;
    const dayShown = date || shownDate.current;

    useEffect(() => { setData(null); setFailed(false); }, [locationId, date]);
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setFailed(false);
        api.get("/dk/day", { params: { location_id: locationId, date } })
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => {
                if (!alive) return;
                const msg = dkMessage(e, lang);
                if (msg) { toast.error(msg); onClose(); } else setFailed(true);
            });
        return () => { alive = false; };
    }, [locationId, date, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const goDay = () => {
        const next = new URLSearchParams(searchParams);
        next.set("section", "dk");
        next.set("tab", "day");
        next.set("loc", locationId);
        next.set("date", date);
        onClose();
        setSearchParams(next);
    };

    const lines = data?.lines || {};
    // an item the location does not count shows only where it still has a stored line (history)
    const items = (data?.items || []).filter((i) => !i.excluded || lines[i.id]);
    const stateText = dataStateText(data?.data_state, scope, lang, t);
    // «اليوم» offers a counter only edit_from..today (DaySheet): an older date would open on today
    const caps = scope?.caps || {};
    const counterOnly = !caps.can_review && !caps.is_master && !caps.can_view_all;
    const dayReachable = !!date && (!scope?.today || date <= scope.today)
        && (!counterOnly || !scope?.edit_from || date >= scope.edit_from);

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" data-testid={P.sheet} className="w-full overflow-y-auto bg-[color:var(--bg-surface)] p-0 sm:max-w-lg">
                <SheetHeader className="sticky top-0 z-10 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-4 py-3 pr-12 text-start sm:text-start">
                    <SheetTitle className="text-base font-extrabold text-[color:var(--ink)]">
                        {locationName || (data?.location ? tn(data.location) : t("dk_panel_title"))}
                    </SheetTitle>
                    <SheetDescription className="text-xs text-[color:var(--ink-muted)]"><bdi>{fmtDay(dayShown, lang)}</bdi></SheetDescription>
                </SheetHeader>
                {failed ? (
                    <div className="p-4"><OfflineBox onRetry={() => setReloadKey((k) => k + 1)} /></div>
                ) : !data ? <InlineLoader /> : (
                    <div className="space-y-3 p-4">
                        {stateText ? (
                            <p className="rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-3 py-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                                {stateText}
                            </p>
                        ) : null}
                        <ul className="space-y-2">
                            {items.map((it) => {
                                const ln = lines[it.id] || {};
                                const unit = unitOf(it, t);
                                const pct = pctOf(ln.unexplained, ln.theoretical, ln.pct);
                                return (
                                    <li key={it.id} data-testid={P.line(it.id)} data-flag={ln.flag ? "true" : "false"}
                                        className={`rounded-[var(--radius-md)] border px-3 py-2 ${ln.flag ? "border-[color:var(--danger-soft-fg)]/40" : "border-[color:var(--border-soft)]"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="min-w-0 text-sm font-bold text-[color:var(--ink)]">{tn(it)}</p>
                                            {ln.flag ? (
                                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[color:var(--danger-soft)] px-2 py-0.5 text-[10px] font-bold text-[color:var(--danger-soft-fg)]">
                                                    <AlertTriangle className="h-3 w-3" />{t("dk_flag")}
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="text-[11px] text-[color:var(--ink-faint)]">
                                            {t("dk_opening")}:{" "}
                                            {ln.opening === null || ln.opening === undefined ? t("dk_opening_none")
                                                : <><bdi dir="ltr">{fmtQuantity(ln.opening, lang)}</bdi> {unit} · {openingSourceText(ln, dayShown, t, lang)}</>}
                                        </p>
                                        <dl className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-1.5">
                                            <Row k="received" label={t("dk_received")} value={fmtQuantity(ln.received ?? 0, lang)} unit={unit} />
                                            <Row k="sent" label={t("dk_sent")} value={ln.sent ? fmtQuantity(ln.sent, lang) : ""} unit={unit} />
                                            <Row k="closing" label={t("dk_closing")} value={fmtQuantity(ln.closing, lang)} unit={unit} />
                                            <Row k="used" label={t("dk_used")} value={fmtQuantity(ln.used, lang)} unit={unit} />
                                            {caps.can_see_system ? (
                                                <Row k="theoretical" label={t("dk_theo")} value={fmtQuantity(ln.theoretical, lang)} unit={unit} />
                                            ) : null}
                                            {caps.can_see_system ? (
                                                <Row k="unexplained" label={t("dk_variance")} unit={pct === null ? unit : `${unit} · ${fmtPct(pct, lang)}`}
                                                     value={fmtSigned(ln.unexplained, lang)}
                                                     tone={ln.flag ? "text-[color:var(--danger-soft-fg)]" : ""} />
                                            ) : null}
                                        </dl>
                                        {ln.note ? <p className="mt-1 break-words text-[11px] text-[color:var(--ink-muted)]">{ln.note}</p> : null}
                                    </li>
                                );
                            })}
                        </ul>
                        {canOpenDay && dayReachable ? (
                            <button type="button" data-testid={P.openDay} onClick={goDay}
                                    className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[color:var(--brand-brown)] text-sm font-bold text-[color:var(--brand-brown)] hover:bg-[color:var(--brand-orange-soft)]">
                                <ClipboardPen className="h-4 w-4" />{t("dk_panel_open_day")}
                            </button>
                        ) : null}
                        <button type="button" data-testid={P.close} onClick={onClose}
                                className="flex h-11 w-full items-center justify-center rounded-full border border-[color:var(--border-soft)] text-sm font-bold text-[color:var(--ink)] hover:bg-[color:var(--bg-surface-2)]">
                            {t("dk_close")}
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
